import { FormEvent, useMemo, useState } from "react";

import { OrganisationTypeConstants } from "../../../../../../constants";
import { SelectData } from "../../../../../../models";
import { useAuth } from "../../../../../../useAuth";
// Imported from its own module rather than the utils barrel, which pulls in the Axios service layer.
import { useUpdateEffect } from "../../../../../../utils/hooks/useUpdateEffect";
import { OBJECT_TYPE_LABELS } from "../../../../../../widget/general/TasksDashboard/constants";
import { NewTaskParentObject } from "../../models";
import { UseNewTaskObjectModalProps, UseNewTaskObjectModalReturnData } from "./models";

/**
 * The kinds a task of this type can hang off, as a label. Read from the portal and the task type
 * rather than from the loaded options, which change with every search term — a label that tracked the
 * current page would rename the field as the user typed.
 *
 * The workflow branch is exactly the two Figma symbols (Seed "Deal or project", Hub "Project"); the
 * general branch also offers landholdings (REQ-007), so it names them.
 */
const buildLabel = (isGeneralTask: boolean, isSeedPortal: boolean): string => {
  const kinds: string[] = [];

  // Deals are Seed-only.
  if (isSeedPortal) kinds.push("deal");
  kinds.push("project");
  // A landholding has no workflow of its own, so it is a general task's parent only.
  if (isGeneralTask) kinds.push("landholding");

  const labels = kinds.map((kind, index) =>
    index === 0 ? OBJECT_TYPE_LABELS[kind] : OBJECT_TYPE_LABELS[kind].toLowerCase()
  );

  if (labels.length === 1) return labels[0];

  return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
};

/**
 * Option labels, guaranteed unique.
 *
 * `Autocomplete` renders each option's *value* as its dropdown text and resolves a selection by
 * matching that text back to the first option carrying it (`useAutocomplete.handleSelect`). Two
 * objects sharing a label are therefore not merely ambiguous to the user — picking the second returns
 * the first one's uuid, and the task is created against the wrong parent.
 *
 * Names collide in practice (two projects called "Peatland restoration"), and nothing else on the row
 * reliably tells them apart, so a collision falls back to a short slice of the uuid. It is only added
 * where a label would otherwise repeat, which keeps it off the overwhelmingly common unique case.
 */
const buildUniqueOptionLabels = (objects: NewTaskParentObject[], includeType: boolean): SelectData => {
  const labels = objects.map((object) =>
    includeType ? `${object.name} · ${OBJECT_TYPE_LABELS[object.type]}` : object.name
  );

  const labelCounts = labels.reduce<Map<string, number>>(
    (counts, label) => counts.set(label, (counts.get(label) ?? 0) + 1),
    new Map()
  );

  return objects.map((object, index) => ({
    key: object.uuid,
    value: (labelCounts.get(labels[index]) ?? 0) > 1 ? `${labels[index]} (${object.uuid.slice(0, 8)})` : labels[index],
  }));
};

export const useNewTaskObjectModal = ({
  show,
  isGeneralTask,
  objects,
  onConfirm,
}: UseNewTaskObjectModalProps): UseNewTaskObjectModalReturnData => {
  const { currentUserType } = useAuth();

  const [selectedObject, setSelectedObject] = useState<NewTaskParentObject>();

  const isSeedPortal = currentUserType === OrganisationTypeConstants.ASSET_MANAGER;

  // The modal stays mounted between openings, so clear the previous choice each time it is reopened.
  useUpdateEffect(() => {
    if (show) setSelectedObject(undefined);
  }, [show]);

  const objectSelectData = useMemo<SelectData>(() => {
    /*
      The type is appended only where the list spans more than one kind: on Hub's workflow branch every
      option is a project, and "Foo · Project" would be noise. Where the kinds are mixed it is what
      keeps a deal and a project of the same name apart.
    */
    const isMixed = new Set(objects.map((object) => object.type)).size > 1;

    return buildUniqueOptionLabels(objects, isMixed);
  }, [objects]);

  const label = useMemo(() => buildLabel(isGeneralTask, isSeedPortal), [isGeneralTask, isSeedPortal]);

  /*
    Held whole rather than as a uuid: the options are a page of search matches, so by the time Confirm is
    pressed the user may have typed again and the chosen object may no longer be among them. The
    autocomplete clears its selection when the text is edited, which arrives here as an empty uuid.
  */
  const handleSelect = (uuid: string): void => {
    setSelectedObject(uuid ? objects.find((object) => object.uuid === uuid) : undefined);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    // Confirm is disabled until a selection is made, so this is a guard rather than a reachable path.
    if (!selectedObject) return;

    onConfirm(selectedObject);
  };

  return {
    objectSelectData,
    selectedObject,
    handleSelect,
    label,
    helperText: `Search for a ${label.toLowerCase()} to create a new task for`,
    handleSubmit,
  };
};
